import React, { useState } from 'react';
import {
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import { useWidth } from '../context/WidthContext';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { motion } from 'framer-motion';
import MagneticButton from './MagneticButton';

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { isFullWidth, toggleWidth } = useWidth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'Work', path: '/work' },
    { text: 'Projects', path: '/projects' },
    { text: 'Skills', path: '/skills' },
    { text: 'About', path: '/about' },
    { text: 'Contact', path: '/contact' },
  ];

  const drawer = (
    <List sx={{ 
      backgroundColor: 'rgba(5, 5, 20, 0.95)',
      height: '100%',
      backdropFilter: 'blur(10px)',
      pt: 4,
    }}>
      {menuItems.map((item) => (
        <ListItem key={item.text} disablePadding>
          <ListItemButton
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            onClick={handleDrawerToggle}
            sx={{
              color: 'text.primary',
              textAlign: 'center',
              py: 2,
              '&.Mui-selected': {
                color: 'primary.main',
                backgroundColor: 'rgba(74, 158, 255, 0.1)',
              },
            }}
          >
            <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600, letterSpacing: '0.1em' }} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      <Paper
        elevation={4}
        component={motion.div}
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        sx={{
          position: 'fixed',
          top: { xs: 16, sm: 24 },
          left: '50%',
          transform: 'translateX(-50%) !important',
          width: { xs: '90%', sm: 'auto' },
          maxWidth: '800px',
          zIndex: 1100,
          backgroundColor: 'rgba(0, 0, 40, 0.6)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '50px',
          px: { xs: 2, sm: 1 },
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Mobile Menu Icon */}
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Desktop Links */}
        <Box 
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}
        >
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            
            return (
              <Box
                key={item.text}
                component={motion.div}
                variants={itemVariants}
                sx={{ display: 'inline-block' }}
              >
                <MagneticButton strength={0.4}>
                  <Box
                    component={Link}
                    to={item.path}
                    sx={{
                      position: 'relative',
                      px: 3,
                      py: 1.5,
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      borderRadius: '30px',
                      transition: 'color 0.3s',
                      display: 'block',
                      '&:hover': {
                        color: '#fff',
                      },
                    }}
                  >
                    {isActive && (
                      <Box
                        component={motion.div}
                        layoutId="activeTab"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: 'rgba(74, 158, 255, 0.15)',
                          borderRadius: '30px',
                          zIndex: -1,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 0 10px rgba(74, 158, 255, 0.3)', // Glow effect
                        }}
                      />
                    )}
                    {item.text}
                  </Box>
                </MagneticButton>
              </Box>
            );
          })}
        </Box>

        {/* Right Side: Width Toggle or Spacer */}
        <Box sx={{ ml: { xs: 'auto', sm: 1 }, mr: 1 }}>
             <IconButton 
              color="inherit" 
              onClick={toggleWidth}
              size="small"
              sx={(theme) => ({
                color: isFullWidth ? 'primary.main' : 'text.secondary',
                backgroundColor: isFullWidth ? 'rgba(74, 158, 255, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              })}
            >
              <AspectRatioIcon fontSize="small" />
            </IconButton>
        </Box>
      </Paper>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="top"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: '100%',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            mt: '80px', // Push below navbar
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.5)', 
            backdropFilter: 'blur(5px)'
          }
        }}
      >
        <Paper sx={{ 
          m: 2, 
          borderRadius: 4, 
          overflow: 'hidden', 
          border: '1px solid rgba(255,255,255,0.1)',
          backgroundColor: 'rgba(0, 0, 40, 0.85)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
        }}>
          {drawer}
        </Paper>
      </Drawer>
    </>
  );
};

export default Navbar;
